import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  FileQuestion,
  Mail,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import { isOpenCascadeStatus } from '@command-center/leadloop-core';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CollectionViewToggle,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  cn,
  useCollectionView,
  usePageHeader,
} from '@command-center/ui-system';
import { LeadLoopIntakeCard, LeadLoopLeadCard } from '@command-center/oneup-portal-ui';
import { leadloopApi, type IntakeItem, type LoopItem } from '../lib/leadloop-api';
import { formsApi } from '../lib/forms-api';
import { aiSystemsApi } from '../lib/ai-systems-api';
import {
  DEFAULT_LOOP_FILTER,
  loopFilterCounts,
  matchesLoopFilter,
  LOOP_FILTER_EMPTY,
  LOOP_FILTER_LABELS,
  type LoopFilter,
} from '../lib/loop-queue';
import { SendEmailDialog } from './SendEmailDialog';
import { IntakeReviewDialog, type IntakeMode } from './IntakeReviewDialog';
import { LeadQuickView } from './LeadQuickView';
import {
  BulkActionBar,
  SelectionCheckbox,
  useBulkSelection,
} from '../components/bulk-record-actions';
import {
  ContactLine,
  ErrorState,
  LeadStatusBadge,
  LoadingState,
  LoopReasonBadge,
  VerifiedTag,
  formatExactDateTime,
  formatRelativeTime,
} from './parts';

const ROLLING_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * LeadLoop — the portal's home.
 *
 * This page used to be two: a Dashboard that answered "how is the week going"
 * and a LeadLoop that answered "what do I do next", each with its own nav item,
 * and the Dashboard duplicated the queue as two recent-record widgets plus a
 * button pointing at the other page. A client signing in had to choose between
 * two screens describing the same work.
 *
 * One page now, in the order the questions are asked: the week's volume at the
 * top, the queue of who needs contact underneath it, and help at the bottom.
 * The recent-calls, recent-form-entries, and recent-activity cards are gone —
 * the queue below them showed the same records, in the same order, with the
 * actions attached.
 */
export function LeadLoopView() {
  usePageHeader(
    { title: 'LeadLoop', subtitle: 'Review what came in, then work what comes next.' },
    [],
  );

  return (
    <div className="mx-auto flex w-full max-w-[1180px] min-w-0 flex-col gap-6">
      <WeekMetrics />
      <ActionableIntake />
      <LoopQueue />
      <HelpDocsCard />
    </div>
  );
}

/* ------------------------------------------------------------- intake desk */

type IntakeFilter = 'all' | 'calls' | 'forms';

const INTAKE_FILTERS: Array<[IntakeFilter, string]> = [
  ['all', 'Everything'],
  ['calls', 'Calls'],
  ['forms', 'Form Entries'],
];

/**
 * The front door: every incoming Call and Form Entry still waiting for a human
 * outcome. AI classification and confidence are shown as advice only; neither
 * one hides a record or makes the decision.
 */
function ActionableIntake() {
  const [filter, setFilter] = useState<IntakeFilter>('all');
  const [view, setView] = useCollectionView('cascade:leadloop:intake', 'list');
  const queryClient = useQueryClient();
  const intake = useQuery({
    queryKey: ['intake'],
    queryFn: leadloopApi.actionableIntake,
  });
  const items = useMemo(() => intake.data?.items ?? [], [intake.data?.items]);
  const counts = useMemo(() => ({
    all: items.length,
    calls: items.filter((item) => item.sourceType === 'call').length,
    forms: items.filter((item) => item.sourceType === 'form_submission').length,
  }), [items]);
  const visible = useMemo(
    () => items.filter((item) => (
      filter === 'all'
        ? true
        : filter === 'calls'
          ? item.sourceType === 'call'
          : item.sourceType === 'form_submission'
    )),
    [filter, items],
  );
  const selection = useBulkSelection(visible.map((item) => item.id));
  const bulk = useMutation({
    mutationFn: (action: 'create_leads' | 'dismiss') => leadloopApi.bulkIntake(
      selection.selectedIds.map((intakeId) => ({ intakeId })),
      action,
      action === 'dismiss' ? { disposition: 'not_qualified' } : {},
    ),
    onSuccess: () => {
      selection.clear();
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['intake'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] }),
        queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] }),
      ]);
    },
  });
  const quickDismiss = useMutation({
    mutationFn: (id: string) => leadloopApi.dismissIntake(id, 'not_qualified'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['intake'] });
    },
  });

  return (
    <section aria-labelledby="actionable-intake-heading" className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="actionable-intake-heading" className="text-base font-semibold text-foreground">
            Actionable Intake
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Calls and form entries waiting for Create Lead, Link Lead, or Dismiss. AI confidence is advisory.
          </p>
        </div>
        {!intake.isLoading && !intake.isError && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5" aria-label="Filter actionable intake">
              {INTAKE_FILTERS.map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={filter === value ? 'default' : 'outline'}
                className="h-8 rounded-full px-3 text-[12px]"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
              >
                {label}
                <span className="ml-1.5 tabular-nums opacity-75">{counts[value]}</span>
              </Button>
              ))}
            </div>
            <CollectionViewToggle view={view} onChange={setView} />
          </div>
        )}
      </div>

      <BulkActionBar count={selection.selectedIds.length} busy={bulk.isPending} onClear={selection.clear}>
        <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate('create_leads')}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Create Leads with AI
        </Button>
        <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => bulk.mutate('dismiss')}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> Dismiss
        </Button>
      </BulkActionBar>
      {bulk.error && (
        <p role="alert" className="text-sm text-destructive">
          {bulk.error instanceof Error ? bulk.error.message : 'The bulk action failed.'}
        </p>
      )}
      {quickDismiss.error && (
        <p role="alert" className="text-sm text-destructive">
          {quickDismiss.error instanceof Error ? quickDismiss.error.message : 'The record could not be dismissed.'}
        </p>
      )}

      {intake.isLoading ? (
        <LoadingState />
      ) : intake.isError ? (
        <ErrorState error={intake.error} />
      ) : visible.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 border-dashed p-8 text-center">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
          <p className="max-w-md text-sm text-muted-foreground">
            {items.length === 0
              ? 'No calls or form entries are waiting for a decision.'
              : 'Nothing is waiting in this record type.'}
          </p>
        </Card>
      ) : view === 'cards' ? (
        <ul className="grid min-w-0 gap-3 md:grid-cols-2">
          {visible.map((item) => (
            <li key={item.id} className="relative min-w-0">
              <span className="absolute left-3 top-3 z-10 rounded bg-background/90 p-1">
                <SelectionCheckbox
                  checked={selection.selected.has(item.id)}
                  onChange={() => selection.toggle(item.id)}
                  label={`Select ${item.displayName ?? 'intake record'}`}
                />
              </span>
              <ActionableIntakeItem item={item} onDismiss={() => quickDismiss.mutate(item.id)} />
            </li>
          ))}
        </ul>
      ) : (
        <ActionableIntakeTable
          items={visible}
          selected={selection.selected}
          allSelected={selection.allSelected}
          onToggle={selection.toggle}
          onToggleAll={selection.toggleAll}
          onDismiss={(id) => quickDismiss.mutate(id)}
        />
      )}
    </section>
  );
}

function ActionableIntakeItem({ item, onDismiss }: { item: IntakeItem; onDismiss: () => void }) {
  const [reviewMode, setReviewMode] = useState<IntakeMode | null>(null);
  const open = (mode: IntakeMode) => setReviewMode(mode);

  return (
    <>
      <IntakeReviewDialog
        intake={item}
        open={reviewMode !== null}
        onOpenChange={(next) => setReviewMode(next ? (reviewMode ?? 'details') : null)}
        mode={reviewMode ?? 'details'}
        onModeChange={setReviewMode}
      />
      <LeadLoopIntakeCard
        item={item}
        onOpen={() => open('details')}
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={(event) => { event.stopPropagation(); onDismiss(); }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Dismiss
            </Button>
          </>
        }
        primaryAction={
          <Button type="button" size="sm" className="gap-1.5" onClick={() => open('create')}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Create Lead with AI
          </Button>
        }
      />
    </>
  );
}

function ActionableIntakeTable({
  items,
  selected,
  allSelected,
  onToggle,
  onToggleAll,
  onDismiss,
}: {
  items: IntakeItem[];
  selected: Set<string>;
  allSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onDismiss: (id: string) => void;
}) {
  return (
    <RecordTable
      minWidth={980}
      columns={[
        {
          key: 'select',
          header: (
            <SelectionCheckbox checked={allSelected} onChange={onToggleAll} label="Select all intake records" />
          ),
          className: 'w-10',
        },
        { key: 'record', header: 'Record' },
        { key: 'type', header: 'Type' },
        { key: 'contact', header: 'Contact', hideOnMobile: true },
        { key: 'received', header: 'Received' },
        { key: 'recommendation', header: 'AI recommendation', hideOnMobile: true },
        { key: 'actions', header: '', align: 'right' },
      ]}
    >
      {items.map((item) => (
        <ActionableIntakeRow
          key={item.id}
          item={item}
          selected={selected.has(item.id)}
          onToggle={() => onToggle(item.id)}
          onDismiss={() => onDismiss(item.id)}
        />
      ))}
    </RecordTable>
  );
}

function ActionableIntakeRow({
  item,
  selected,
  onToggle,
  onDismiss,
}: {
  item: IntakeItem;
  selected: boolean;
  onToggle: () => void;
  onDismiss: () => void;
}) {
  const [reviewMode, setReviewMode] = useState<IntakeMode | null>(null);
  return (
    <>
      <IntakeReviewDialog
        intake={item}
        open={reviewMode !== null}
        onOpenChange={(next) => setReviewMode(next ? (reviewMode ?? 'details') : null)}
        mode={reviewMode ?? 'details'}
        onModeChange={setReviewMode}
      />
      <RecordTableRow selected={selected} flagged onOpen={() => setReviewMode('details')}>
        <RecordTableCell>
          <SelectionCheckbox
            checked={selected}
            onChange={onToggle}
            label={`Select ${item.displayName ?? 'intake record'}`}
          />
        </RecordTableCell>
        <RecordTablePrimaryCell
          title={item.displayName ?? item.company ?? 'Unknown contact'}
          subtitle={item.summary ?? undefined}
        />
        <RecordTableCell>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {item.sourceType === 'call' ? 'Call' : 'Form Entry'}
          </span>
        </RecordTableCell>
        <RecordTableCell hideOnMobile>
          <ContactLine phone={item.phone ?? undefined} email={item.email ?? undefined} stacked />
        </RecordTableCell>
        <RecordTableCell>
          <span className="block whitespace-nowrap tabular-nums text-foreground">
            {formatExactDateTime(item.occurredAt ?? item.createdAt) ?? '—'}
          </span>
          <span className="block whitespace-nowrap text-[11px] text-muted-foreground">
            {formatRelativeTime(item.occurredAt ?? item.createdAt)}
          </span>
        </RecordTableCell>
        <RecordTableCell hideOnMobile>
          <span className="block max-w-[240px] truncate text-xs text-foreground">
            {item.aiRecommendation ?? item.leadEligibility ?? 'Needs review'}
          </span>
          {item.aiConfidence !== null && (
            <span className="text-[11px] text-muted-foreground">
              {Math.round(item.aiConfidence * 100)}% confidence
            </span>
          )}
        </RecordTableCell>
        <RecordTableCell align="right">
          <div className="flex justify-end gap-1.5" onClick={(event) => event.stopPropagation()}>
            <Button size="sm" variant="outline" onClick={onDismiss}>Dismiss</Button>
            <Button size="sm" onClick={() => setReviewMode('create')}>Create with AI</Button>
          </div>
        </RecordTableCell>
      </RecordTableRow>
    </>
  );
}

/* ------------------------------------------------------------------ metrics */

interface Metric {
  label: string;
  value: number;
  loading: boolean;
  unavailable: boolean;
}

/**
 * Four numbers, each independently degradable: a feed outage dims one tile
 * rather than blanking the page the client works from.
 */
function WeekMetrics() {
  // Pinned once per mount so the four queries share one boundary and the cache
  // key does not churn on every render.
  const weekStart = useMemo(() => new Date(Date.now() - ROLLING_WEEK_MS).toISOString(), []);

  const leads = useQuery({
    queryKey: ['leadloop-home', 'leads'],
    queryFn: () => leadloopApi.leads({ status: 'all' }),
  });
  const weeklyCalls = useQuery({
    queryKey: ['leadloop-home', 'weekly-calls', weekStart],
    queryFn: () => aiSystemsApi.calls({ dateFrom: weekStart, limit: 100 }),
  });
  const weeklySubmissions = useQuery({
    queryKey: ['leadloop-home', 'weekly-form-entries', weekStart],
    // A volume count, so it stays every submission received — triage separates
    // the feed, it does not change how many entries arrived.
    queryFn: () => formsApi.submissions({ triage: 'all', dateFrom: weekStart, limit: 1000 }),
  });

  const stats: Metric[] = [
    {
      label: 'Calls · last 7 days',
      value: weeklyCalls.data?.items.length ?? 0,
      loading: weeklyCalls.isLoading,
      unavailable: weeklyCalls.isError,
    },
    {
      label: 'Form entries · last 7 days',
      value: weeklySubmissions.data?.items.length ?? 0,
      loading: weeklySubmissions.isLoading,
      unavailable: weeklySubmissions.isError,
    },
    {
      label: 'Leads created · last 7 days',
      value: countSince(leads.data?.items, (item) => item.createdAt, Date.parse(weekStart)),
      loading: leads.isLoading,
      unavailable: leads.isError,
    },
    {
      label: 'Total active leads',
      value: leads.data?.items.filter((lead) => isOpenCascadeStatus(lead.status)).length ?? 0,
      loading: leads.isLoading,
      unavailable: leads.isError,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      {stats.map((stat) => (
        <MetricCard key={stat.label} metric={stat} />
      ))}
    </div>
  );
}

function MetricCard({ metric }: { metric: Metric }) {
  const value = metric.loading ? '…' : metric.unavailable ? '—' : metric.value.toLocaleString();
  return (
    <Card title={metric.unavailable ? `${metric.label} is temporarily unavailable.` : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {metric.label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <span className="text-2xl font-semibold text-foreground">{value}</span>
        {metric.unavailable && <span className="sr-only">Temporarily unavailable</span>}
      </CardContent>
    </Card>
  );
}

function countSince<T>(
  items: T[] | undefined,
  dateOf: (item: T) => string | undefined,
  since: number,
): number {
  return (items ?? []).filter((item) => {
    const timestamp = Date.parse(dateOf(item) ?? '');
    return Number.isFinite(timestamp) && timestamp >= since;
  }).length;
}

function HelpDocsCard() {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4 sm:items-center">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <FileQuestion className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground">Help Docs</p>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Cascade guides and workflow references are being prepared for launch.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------- queue */

/** The follow-up queue for Leads that have already passed through intake. */
function LoopQueue() {
  const [filter, setFilter] = useState<LoopFilter>(DEFAULT_LOOP_FILTER);
  const [view, setView] = useCollectionView('cascade:leadloop:collection', 'list');
  const queryClient = useQueryClient();
  const loop = useQuery({ queryKey: ['loop'], queryFn: leadloopApi.loop });
  const items = useMemo(() => loop.data?.items ?? [], [loop.data?.items]);
  const counts = useMemo(() => loopFilterCounts(items), [items]);

  const active = filter;
  const visibleItems = useMemo(
    () => items.filter((item) => matchesLoopFilter(item, active)),
    [active, items],
  );
  const selection = useBulkSelection(visibleItems.map((item) => item.lead.id));
  const bulkDismiss = useMutation({
    mutationFn: () => leadloopApi.bulkDismissLeads(selection.selectedIds),
    onSuccess: () => {
      selection.clear();
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
      ]);
    },
  });

  if (loop.isLoading) return <LoadingState />;
  if (loop.isError) return <ErrorState error={loop.error} />;

  return (
    <section aria-label="LeadLoop queue" className="flex min-w-0 flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold text-foreground">Lead Follow-up</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Leads that need attention now or are waiting on a scheduled follow-up.
        </p>
      </div>
      {/*
        The tabs are already in queue order and carry counts; a reader who can
        see "Needs Attention 4 · Scheduled 2" does not need a paragraph
        describing how to work them.
      */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" aria-label="Filter LeadLoop">
          {LOOP_FILTER_LABELS.map(([value, label]) => {
            const selected = active === value;
            // An empty tab stays reachable but stops competing for attention:
            // removing it would make a returning user wonder where intake
            // went, and dimming it says "nothing here" before it is clicked.
            const empty = counts[value] === 0;
            return (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={selected ? 'default' : 'outline'}
                className={cn(
                  'h-8 rounded-full px-3 text-[12px]',
                  !selected && empty && 'border-dashed text-muted-foreground opacity-60 hover:opacity-100',
                )}
                aria-pressed={selected}
                onClick={() => setFilter(value)}
              >
                {label}
                {counts[value] > 0 && (
                  <span className="ml-1.5 tabular-nums opacity-75">{counts[value]}</span>
                )}
              </Button>
            );
          })}
        </div>
        <CollectionViewToggle view={view} onChange={setView} />
      </div>

      <BulkActionBar
        count={selection.selectedIds.length}
        busy={bulkDismiss.isPending}
        onClear={selection.clear}
      >
        <Button
          size="sm"
          variant="outline"
          disabled={bulkDismiss.isPending}
          onClick={() => bulkDismiss.mutate()}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> Dismiss Leads
        </Button>
      </BulkActionBar>
      {bulkDismiss.error && (
        <p role="alert" className="text-sm text-destructive">
          {bulkDismiss.error instanceof Error ? bulkDismiss.error.message : 'The Leads could not be dismissed.'}
        </p>
      )}

      {items.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 border-dashed p-8 text-center">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
          <p className="max-w-md text-sm text-muted-foreground">
            Nothing needs contact right now — the loop is clear.
          </p>
        </Card>
      ) : visibleItems.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 border-dashed p-8 text-center">
          {active === 'attention' && (
            <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
          )}
          <p className="max-w-md text-sm text-muted-foreground">{LOOP_FILTER_EMPTY[active]}</p>
          {/* An empty Needs Attention is good news, and the next question is what
              is still in play — so point at it rather than leaving a dead end. */}
          {active === 'attention' && counts.scheduled > 0 && (
            <Button type="button" size="sm" variant="outline" onClick={() => setFilter('scheduled')}>
              See what is scheduled ({counts.scheduled})
            </Button>
          )}
        </Card>
      ) : view === 'cards' ? (
        <ul className="grid min-w-0 gap-3 md:grid-cols-2">
          {visibleItems.map((item) => (
            <li key={`lead:${item.lead.id}`} className="relative min-w-0">
              <span className="absolute left-3 top-3 z-10 rounded bg-background/90 p-1">
                <SelectionCheckbox
                  checked={selection.selected.has(item.lead.id)}
                  onChange={() => selection.toggle(item.lead.id)}
                  label={`Select ${item.lead.title}`}
                />
              </span>
              <LeadLoopCard item={item} />
            </li>
          ))}
        </ul>
      ) : (
        <LoopTable
          items={visibleItems}
          selected={selection.selected}
          allSelected={selection.allSelected}
          onToggle={selection.toggle}
          onToggleAll={selection.toggleAll}
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------- table */

/**
 * The queue as rows.
 *
 * Six columns, chosen so a row answers what to do without opening it: who it
 * was, what kind of record, how to reach them, exactly when it arrived, where
 * it stands, and the one control that opens it. Everything else — transcript,
 * submitted fields, notes, analysis — lives inside the record.
 */
function LoopTable({
  items,
  selected,
  allSelected,
  onToggle,
  onToggleAll,
}: {
  items: LoopItem[];
  selected: Set<string>;
  allSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}) {
  return (
    <RecordTable
      minWidth={900}
      columns={[
        {
          key: 'select',
          header: <SelectionCheckbox checked={allSelected} onChange={onToggleAll} label="Select all Leads" />,
          className: 'w-10',
        },
        { key: 'record', header: 'Record' },
        { key: 'type', header: 'Type' },
        { key: 'contact', header: 'Contact', hideOnMobile: true },
        { key: 'when', header: 'Received' },
        { key: 'state', header: 'Status' },
        { key: 'open', header: '', align: 'right' },
      ]}
    >
      {items.map((item) => (
        <LeadRow
          key={`lead:${item.lead.id}`}
          item={item}
          selected={selected.has(item.lead.id)}
          onToggle={() => onToggle(item.lead.id)}
        />
      ))}
    </RecordTable>
  );
}

function LeadRow({ item, selected, onToggle }: { item: LoopItem; selected: boolean; onToggle: () => void }) {
  const { lead } = item;
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  return (
    <>
      <LeadQuickView
        lead={lead}
        reason={item.reason}
        dueAt={item.dueAt}
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
      />
      <RecordTableRow selected={selected} onOpen={() => setQuickViewOpen(true)}>
        <RecordTableCell>
          <SelectionCheckbox checked={selected} onChange={onToggle} label={`Select ${lead.title}`} />
        </RecordTableCell>
        <RecordTablePrimaryCell
          title={lead.title}
          subtitle={lead.contactName ?? lead.companyName ?? lead.aiSummary ?? undefined}
        />
        <RecordTableCell>
          <TypePill />
        </RecordTableCell>
        <RecordTableCell hideOnMobile>
          <ContactLine phone={lead.phone} email={lead.email} stacked />
        </RecordTableCell>
        <RecordTableCell>
          <span className="block whitespace-nowrap tabular-nums text-foreground">
            {formatExactDateTime(lead.createdAt) ?? '—'}
          </span>
          <span className="block whitespace-nowrap text-[11px] text-muted-foreground">
            {formatRelativeTime(lead.createdAt)}
          </span>
        </RecordTableCell>
        <RecordTableCell>
          <div className="flex flex-wrap items-center gap-1.5">
            <LeadStatusBadge status={lead.status} showActive />
            <LoopReasonBadge reason={item.reason} />
            <VerifiedTag verified={lead.verified} />
          </div>
        </RecordTableCell>
        <RecordTableCell align="right">
          <Button type="button" size="sm" className="gap-1.5" asChild>
            <Link to={`/leads/${lead.id}`} onClick={(event) => event.stopPropagation()}>
              Open
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        </RecordTableCell>
      </RecordTableRow>
    </>
  );
}

/** One quiet label per row. Kept as a component so every row spells it once. */
function TypePill() {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-muted-foreground">
      <User className="h-3.5 w-3.5 shrink-0" aria-hidden />
      Lead
    </span>
  );
}

/* -------------------------------------------------------------------- cards */

/**
 * One queue card.
 *
 * The card itself is `LeadLoopLeadCard` from @command-center/oneup-portal-ui —
 * the shared Lead card both portals draw — so the badge set, the meta row, the
 * flagged treatment, and the "what does this want next" highlight are one
 * implementation. What stays here is what is Cascade's: the quick view it opens
 * and the email composer on its footer.
 */
function LeadLoopCard({ item }: { item: LoopItem }) {
  const { lead } = item;
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  return (
    <>
      {/*
        The dialog is a SIBLING of the card, not a child — the same constraint
        the intake card used to document. DialogContent portals to document.body, but React
        synthetic events bubble through the COMPONENT tree, so a dialog living
        inside the card would close on its own close button and then have that
        click continue into the card's onClick and reopen it.
      */}
      <LeadQuickView
        lead={lead}
        reason={item.reason}
        dueAt={item.dueAt}
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
      />
      <LeadLoopLeadCard
        lead={lead}
        reason={item.reason}
        dueAt={item.dueAt}
        onOpen={() => setQuickViewOpen(true)}
        /*
          Two controls, because a card is for deciding which lead to work, not
          for working it. Notes, the follow-up date, Verified, Hand Off, and
          Dismiss all live in one Action Center — opening the card reaches it in
          a click, and copies of it on every row would bury the feed under the
          same form twenty times over.
        */
        actions={
          lead.email ? (
            <SendEmailDialog
              leadId={lead.id}
              trigger={
                <Button type="button" size="sm" variant="outline" className="gap-1.5">
                  <Mail className="h-3.5 w-3.5" aria-hidden />
                  Email
                </Button>
              }
            />
          ) : (
            <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled>
              <Mail className="h-3.5 w-3.5" aria-hidden />
              Email
            </Button>
          )
        }
        primaryAction={
          <Button type="button" size="sm" className="gap-1.5" asChild>
            <Link to={`/leads/${lead.id}`}>
              Open
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        }
      />
    </>
  );
}
